import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc } from '@goaltree/backend/convex/_generated/dataModel'
import { type CelebrationStyle, resolveCelebration } from '@goaltree/backend/convex/lib/celebration'
import { DEFAULT_DAY_START_HOUR } from '@goaltree/backend/convex/lib/day'
import { DISPLAY_NAME_MAX, publicName, shortName } from '@goaltree/backend/convex/lib/displayName'
import { FOCUSES } from '@goaltree/backend/convex/lib/focus'
import {
  NIGHT_OWL_DAY_START_HOUR,
  dayStartHourFor,
} from '@goaltree/backend/convex/lib/onboardingFlow'
import {
  type TodaySection as TodaySectionKey,
  resolveTodaySectionOrder,
} from '@goaltree/backend/convex/lib/todaySections'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Calendar } from '@goaltree/ui/components/calendar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@goaltree/ui/components/select'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAction, useConvexAuth, useMutation, useQuery } from 'convex/react'
import {
  AtSignIcon,
  CalendarIcon,
  CameraIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CodeIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FlameIcon,
  GlobeIcon,
  GripVerticalIcon,
  KeyRoundIcon,
  Loader2,
  LockIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  PaletteIcon,
  PauseIcon,
  PencilIcon,
  SearchIcon,
  SparklesIcon,
  SproutIcon,
  StarIcon,
  SunIcon,
  TargetIcon,
  TrashIcon,
  TrophyIcon,
  UserIcon,
  UsersIcon,
  XIcon,
  ZapIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AccentSwatches, useAccentPicker } from '@/components/accent-swatches'
import { AppConnectionsSettings } from '@/components/app-connections'
import { ReviewDialog } from '@/components/app-review'
import { AppSidebar } from '@/components/app-sidebar'
import { CalendarSettings } from '@/components/calendar-settings'
import { CanopyAvatar } from '@/components/canopy-avatar'
import { CelebrationChips } from '@/components/celebration-picker'
import { CoachingModeSettings } from '@/components/coaching/coaching-mode'
import { DayShareSettings, QuickLinksSettings } from '@/components/day-share'
import { BadgesDialog, GrowthSettingsSummary } from '@/components/growth-section'
import { LocationSettings } from '@/components/location-settings'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { BlockedSettings } from '@/components/settings/blocked-settings'
import { CoachSection } from '@/components/settings/coach-settings'
import { EmailToGoaltreeSettings } from '@/components/settings/email-to-goaltree'
import { MyCoachSection } from '@/components/settings/my-coach-settings'
import { NOTIF_GROUPS, NotificationsSection } from '@/components/settings/notification-settings'
import { PlanSection } from '@/components/settings/plan-usage'
import {
  SETTINGS_SECTIONS,
  SettingsGroup,
  SettingsRow,
  SettingsSection,
  type SettingsSectionId,
  Toggle,
  formatHour,
  range,
  rowId,
} from '@/components/settings/primitives'
import { WearablesSettings } from '@/components/settings/wearables-settings'
import { SiteHeader } from '@/components/site-header'
import { useTheme } from '@/components/theme-provider'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { useBreather } from '@/hooks/use-breather'
import { useHabitCheckStyle, useSetHabitCheckStyle } from '@/hooks/use-habit-check-style'
import { useIsPro } from '@/hooks/use-is-pro'
import { useScrollTitle } from '@/hooks/use-scroll-title'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { useWebPush } from '@/hooks/use-web-push'
import i18n, { LANG_STORAGE_KEY } from '@/i18n'
import { authClient } from '@/lib/auth-client'
import { dicebearUrl } from '@/lib/avatar'
import { playCelebrationSound } from '@/lib/celebrate'
import { useIsCoaching } from '@/lib/coaching-mode'
import { clearOfflineData } from '@/lib/offline-cache'
import { optimisticSetBio, optimisticSetHandle, optimisticSetLocale } from '@/lib/prefs-optimistic'
import { clearSessionIndicator } from '@/lib/session-indicator'
import { useViewStore } from '@/store/view.store'

export const Route = createFileRoute('/_authenticated/settings')({
  head: () => ({
    meta: [{ title: 'Settings — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

type Prefs = Partial<Doc<'userPreferences'>>
const usePrefs = () => useQuery(api.preferences.getMyPreferences) as Prefs | undefined

// ── Profile ───────────────────────────────────────────────────────────────────

function ProfileSection() {
  const { t } = useTranslation()
  const [badgesOpen, setBadgesOpen] = useState(false)
  const { isAuthenticated } = useConvexAuth()
  const user = useQuery(api.auth.getCurrentUser, isAuthenticated ? {} : 'skip')
  const { data: session } = authClient.useSession()
  const currentHandle = useQuery(api.preferences.getMyHandle)
  const setHandle = useMutation(api.preferences.setHandle).withOptimisticUpdate(optimisticSetHandle)
  const currentBio = useQuery(api.preferences.getMyBio)
  const setBio = useMutation(api.preferences.setBio).withOptimisticUpdate(optimisticSetBio)

  // Shown right away on save; dropped once the server copy catches up (or fails).
  const [pendingName, setPendingName] = useState<string | null>(null)
  useEffect(() => {
    if (pendingName !== null && user?.name === pendingName) setPendingName(null)
  }, [user?.name, pendingName])
  const displayName = pendingName ?? user?.name
  const displayEmail = user?.email
  const displayHandle = currentHandle
  const displayBio = currentBio

  const [editing, setEditing] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const [editingHandle, setEditingHandle] = useState(false)
  const [handleValue, setHandleValue] = useState('')
  const [savingHandle, setSavingHandle] = useState(false)
  const handleInputRef = useRef<HTMLInputElement>(null)

  const [editingBio, setEditingBio] = useState(false)
  const [bioValue, setBioValue] = useState('')
  const [savingBio, setSavingBio] = useState(false)

  const generateUploadUrl = useMutation(api.profilePhoto.generateUploadUrl)
  const resolveUploadedUrl = useMutation(api.profilePhoto.resolveUploadedUrl)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  async function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.photoTypeError'))
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.photoSizeError'))
      return
    }
    setUploadingPhoto(true)
    try {
      const uploadUrl = await generateUploadUrl()
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      if (!res.ok) throw new Error('upload failed')
      const { storageId } = (await res.json()) as { storageId: string }
      const url = await resolveUploadedUrl({ storageId: storageId as never })
      if (!url) throw new Error('no url')
      const { error } = await authClient.updateUser({ image: url })
      if (error) throw new Error('update failed')
      toast.success(t('settings.photoSaved'))
    } catch {
      toast.error(t('settings.photoError'))
    } finally {
      setUploadingPhoto(false)
    }
  }

  // A DiceBear image means "no custom photo" — deleting reverts to a fresh one
  // (never to initials).
  const dicebearImage = dicebearUrl(user?.email || user?.name || '')
  const hasCustomPhoto = !!user?.image && !user.image.includes('api.dicebear.com')

  async function handleRemovePhoto() {
    setUploadingPhoto(true)
    try {
      const { error } = await authClient.updateUser({ image: dicebearImage })
      if (error) throw new Error('update failed')
      toast.success(t('settings.photoRemoved'))
    } catch {
      toast.error(t('settings.photoError'))
    } finally {
      setUploadingPhoto(false)
    }
  }

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  function startEdit() {
    setNameValue(displayName ?? '')
    setEditing(true)
  }

  async function saveName() {
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === displayName) {
      setEditing(false)
      return
    }
    setPendingName(trimmed)
    setEditing(false)
    const { error } = await authClient.updateUser({ name: trimmed })
    if (error) {
      setPendingName(null)
      toast.error(t('settings.nameError'))
    } else toast.success(t('settings.nameSaved'))
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') saveName()
    if (e.key === 'Escape') setEditing(false)
  }

  function startEditHandle() {
    setHandleValue(currentHandle ?? '')
    setEditingHandle(true)
    setTimeout(() => handleInputRef.current?.select(), 0)
  }

  async function saveHandle() {
    const trimmed = handleValue.trim().toLowerCase()
    if (!trimmed || trimmed === currentHandle) {
      setEditingHandle(false)
      return
    }
    setSavingHandle(true)
    try {
      await setHandle({ handle: trimmed })
      toast.success(t('settings.handleSaved'))
      setEditingHandle(false)
    } catch (e: unknown) {
      const data = (e as { data?: string })?.data
      if (data === 'Handle already taken.') {
        toast.error(t('settings.handleTaken'))
      } else if (typeof data === 'string' && data.includes('Handle must be')) {
        toast.error(t('settings.handleInvalid'))
      } else {
        toast.error(t('settings.handleInvalid'))
      }
    } finally {
      setSavingHandle(false)
    }
  }

  function handleHandleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') saveHandle()
    if (e.key === 'Escape') setEditingHandle(false)
  }

  async function saveBio() {
    const trimmed = bioValue.trim()
    if (trimmed === (currentBio ?? '')) {
      setEditingBio(false)
      return
    }
    setSavingBio(true)
    try {
      await setBio({ bio: trimmed })
      toast.success('Bio saved')
      setEditingBio(false)
    } catch {
      toast.error('Could not save bio')
    } finally {
      setSavingBio(false)
    }
  }

  const imageUrl = session?.user?.image ?? user?.image ?? null

  return (
    <SettingsSection id="profile" title={t('settings.sec.profile')}>
      <SettingsGroup>
        <div className="flex items-center gap-4 px-4 py-3.5">
          <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <UserIcon className="h-4 w-4" />
          </span>

          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  value={nameValue}
                  onChange={(e) =>
                    setNameValue(
                      e.target.value.replace(
                        /(^|\s)(\p{L})/gu,
                        (_, sep, ch) => sep + ch.toUpperCase(),
                      ),
                    )
                  }
                  onKeyDown={handleKeyDown}
                  onBlur={saveName}
                  className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-sm font-medium outline-none focus:ring-2 focus:ring-primary/40"
                  autoCapitalize="words"
                  autoComplete="name"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={saveName}
                  className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary text-white dark:text-[#0a0a0a] hover:bg-primary/90"
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md hover:bg-muted"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <p className="text-sm font-medium">
                {displayName === undefined ? (
                  <span className="inline-block h-4 w-32 animate-pulse rounded bg-muted" />
                ) : (
                  (displayName ?? '—')
                )}
              </p>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {displayEmail === undefined ? (
                <span className="inline-block h-3 w-44 animate-pulse rounded bg-muted" />
              ) : (
                (displayEmail ?? '')
              )}
            </p>
          </div>

          {!editing && (
            <div className="flex items-center gap-2">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <button
                      type="button"
                      disabled={uploadingPhoto}
                      className="group relative shrink-0 cursor-pointer rounded-full"
                      aria-label={t('settings.changePhoto')}
                      title={t('settings.changePhoto')}
                    />
                  }
                >
                  <CanopyAvatar userId={user?._id}>
                    <UserAvatar
                      image={imageUrl}
                      email={user?.email}
                      name={user?.name}
                      className="size-9 rounded-full object-cover"
                    />
                  </CanopyAvatar>
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                    <CameraIcon className="h-3.5 w-3.5 text-white" />
                  </span>
                  {uploadingPhoto && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
                    </span>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => photoInputRef.current?.click()}>
                    <CameraIcon className="h-4 w-4" />
                    {t('settings.changePhoto')}
                  </DropdownMenuItem>
                  {hasCustomPhoto && (
                    <DropdownMenuItem variant="destructive" onClick={handleRemovePhoto}>
                      <TrashIcon className="h-4 w-4" />
                      {t('settings.removePhoto')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                type="button"
                onClick={startEdit}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Handle row */}
        <div className="flex items-center gap-4 px-4 py-3.5">
          <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <AtSignIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            {editingHandle ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">@</span>
                <input
                  ref={handleInputRef}
                  value={handleValue}
                  onChange={(e) =>
                    setHandleValue(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))
                  }
                  onKeyDown={handleHandleKeyDown}
                  onBlur={saveHandle}
                  disabled={savingHandle}
                  placeholder="your-handle"
                  className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-sm font-medium outline-none focus:ring-2 focus:ring-primary/40"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={saveHandle}
                  disabled={savingHandle}
                  className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary text-white dark:text-[#0a0a0a] hover:bg-primary/90"
                >
                  {savingHandle ? (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <CheckIcon className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingHandle(false)}
                  className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md hover:bg-muted"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <p className="text-sm font-medium">
                {displayHandle === undefined ? (
                  <span className="inline-block h-4 w-28 animate-pulse rounded bg-muted" />
                ) : displayHandle ? (
                  `@${displayHandle}`
                ) : (
                  <span className="text-muted-foreground">No handle set</span>
                )}
              </p>
            )}
            {displayHandle === undefined ? (
              <span className="mt-0.5 inline-block h-3 w-36 animate-pulse rounded bg-muted" />
            ) : displayHandle ? (
              <a
                href={`https://gt.today/${displayHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-0.5 text-xs text-primary-text hover:underline"
              >
                gt.today/{displayHandle}
              </a>
            ) : (
              <p className="mt-0.5 text-xs text-muted-foreground">gt.today/handle</p>
            )}
          </div>
          {!editingHandle && (
            <button
              type="button"
              onClick={startEditHandle}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Bio row */}
        <div className="flex items-start gap-4 px-4 py-3.5">
          <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground mt-0.5">
            <PencilIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            {editingBio ? (
              <div className="flex flex-col gap-2">
                <textarea
                  value={bioValue}
                  onChange={(e) => setBioValue(e.target.value.slice(0, 200))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingBio(false)
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveBio()
                  }}
                  disabled={savingBio}
                  rows={3}
                  placeholder={t('posts.bioPlaceholder')}
                  className="w-full rounded-md border bg-background px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{bioValue.length}/200</span>
                  <button
                    type="button"
                    onClick={saveBio}
                    disabled={savingBio}
                    className="ml-auto flex h-7 cursor-pointer items-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-white dark:text-[#0a0a0a] hover:bg-primary/90"
                  >
                    {savingBio ? (
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <CheckIcon className="h-3 w-3" />
                    )}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingBio(false)}
                    className="flex h-7 cursor-pointer items-center rounded-md px-2 text-xs text-muted-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm">
                {displayBio === undefined ? (
                  <span className="inline-block h-4 w-48 animate-pulse rounded bg-muted" />
                ) : (
                  displayBio || <span className="text-muted-foreground">No bio yet</span>
                )}
              </p>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.publicProfile')}</p>
          </div>
          {!editingBio && (
            <button
              type="button"
              onClick={() => {
                setBioValue(currentBio ?? '')
                setEditingBio(true)
              }}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div id={rowId('progression.section')} className="border-t border-border/60">
          <GrowthSettingsSummary onOpen={() => setBadgesOpen(true)} />
        </div>
      </SettingsGroup>
      <NamePrivacySettings fullName={displayName ?? session?.user?.name ?? ''} />
      <LiveSettings />
      <LeaderboardSettings />
      <DayShareSettings />
      <QuickLinksSettings />
      <BlockedSettings />
      <BadgesDialog open={badgesOpen} onOpenChange={setBadgesOpen} />
    </SettingsSection>
  )
}

function NamePrivacySettings({ fullName }: { fullName: string }) {
  const { t } = useTranslation()
  const prefs = usePrefs()
  const updatePrefs = useUpdatePreferences()
  const setDisplayName = useMutation(api.preferences.setDisplayName)
  const saved = prefs?.displayName ?? ''
  const [value, setValue] = useState(saved)
  useEffect(() => setValue(saved), [saved])
  const showFull = prefs?.showFullName === true
  const shown = publicName(fullName, { displayName: prefs?.displayName, showFullName: showFull })
  const description =
    showFull || saved
      ? t('settings.nameShowFullOn', { name: shown })
      : t('settings.nameShowFullOff', { name: shortName(fullName) })
  const save = async (next: string) => {
    const trimmed = next.trim()
    if (trimmed === saved) return setValue(saved)
    try {
      await setDisplayName({ displayName: trimmed || null })
      toast.success(t('settings.nameDisplaySaved'))
    } catch {
      setValue(saved)
      toast.error(t('settings.nameDisplayError'))
    }
  }
  return (
    <SettingsGroup label={t('settings.namePrivacy')}>
      <SettingsRow
        icon={<UserIcon className="h-4 w-4" />}
        label={t('settings.nameShowFull')}
        description={description}
      >
        <Toggle
          label={t('settings.nameShowFull')}
          checked={showFull}
          onChange={(v) =>
            void updatePrefs({ showFullName: v }).catch(() => toast.error(t('settings.prefError')))
          }
          disabled={prefs === undefined}
        />
      </SettingsRow>
      <SettingsRow
        icon={<PencilIcon className="h-4 w-4" />}
        label={t('settings.nameDisplay')}
        description={t('settings.nameDisplayHelp')}
      >
        <div className="flex items-center gap-1">
          <input
            value={value}
            maxLength={DISPLAY_NAME_MAX}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => void save(value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
            disabled={prefs === undefined}
            placeholder={shortName(fullName)}
            className="w-40 rounded-md border bg-background px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          {value && (
            <button
              type="button"
              aria-label={t('settings.nameDisplayClear')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setValue('')
                void save('')
              }}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </SettingsRow>
    </SettingsGroup>
  )
}

function LiveSettings() {
  const { t } = useTranslation()
  const prefs = usePrefs()
  const updatePrefs = useUpdatePreferences()
  const save = (patch: { liveFriends?: boolean; liveGlobal?: boolean }) =>
    void updatePrefs(patch).catch(() => toast.error(t('settings.prefError')))
  return (
    <SettingsGroup label={t('settings.live.title')}>
      <SettingsRow
        id={rowId('settings.live.friends')}
        icon={<UsersIcon className="h-4 w-4" />}
        label={t('settings.live.friends')}
        description={t('settings.live.friendsDesc')}
      >
        <Toggle
          label={t('settings.live.friends')}
          checked={prefs?.liveFriends !== false}
          onChange={(v) => save({ liveFriends: v })}
          disabled={prefs === undefined}
        />
      </SettingsRow>
      <SettingsRow
        id={rowId('settings.live.global')}
        icon={<GlobeIcon className="h-4 w-4" />}
        label={t('settings.live.global')}
        description={t('settings.live.globalDesc')}
      >
        <Toggle
          label={t('settings.live.global')}
          checked={prefs?.liveGlobal !== false}
          onChange={(v) => save({ liveGlobal: v })}
          disabled={prefs === undefined}
        />
      </SettingsRow>
    </SettingsGroup>
  )
}

function LeaderboardSettings() {
  const { t } = useTranslation()
  const prefs = usePrefs()
  const updatePrefs = useUpdatePreferences()
  const coaching = (useQuery(api.coaching.myClientCount) ?? 0) > 0
  const pinned = (prefs as { showOnLeaderboard?: boolean } | undefined)?.showOnLeaderboard
  return (
    <SettingsGroup label={t('nav.leaderboard')}>
      <SettingsRow
        id={rowId('settings.leaderboard.show')}
        icon={<TrophyIcon className="h-4 w-4" />}
        label={t('settings.leaderboard.show')}
        description={t(
          coaching ? 'settings.leaderboard.coachDesc' : 'settings.leaderboard.showDesc',
        )}
      >
        <Toggle
          label={t('settings.leaderboard.show')}
          checked={pinned ?? !coaching}
          onChange={(v) =>
            void updatePrefs({ showOnLeaderboard: v }).catch(() =>
              toast.error(t('settings.prefError')),
            )
          }
          disabled={prefs === undefined}
        />
      </SettingsRow>
    </SettingsGroup>
  )
}

// ── Appearance & language ─────────────────────────────────────────────────────

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'pt', label: 'Português' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文' },
]

// Check-off celebration style (tap to preview) and its optional sound.
function CelebrationRows({ onLocked }: { onLocked: () => void }) {
  const { t } = useTranslation()
  const prefs = usePrefs() as
    | { celebrationStyle?: CelebrationStyle; celebrationSound?: boolean }
    | undefined
  const updatePrefs = useUpdatePreferences()
  const { isPro } = useIsPro()
  const current = prefs?.celebrationStyle ?? 'classic'
  return (
    <>
      {/* Label and hint, then the styles on their own full-width row. */}
      <div id={rowId('settings.celebration')} className="scroll-mt-24">
        <SettingsRow
          icon={<SparklesIcon className="h-4 w-4" />}
          label={t('settings.celebration')}
          description={t('settings.celebrationDesc')}
        />
        <CelebrationChips onLocked={onLocked} className="-mt-1.5 px-4 pb-3.5 sm:pl-16" />
      </div>
      <SettingsRow
        id={rowId('settings.celebrationSound')}
        label={t('settings.celebrationSound')}
        description={t('settings.celebrationSoundDesc')}
      >
        <Toggle
          label={t('settings.celebrationSound')}
          checked={!!prefs?.celebrationSound}
          onChange={(v) => {
            void updatePrefs({ celebrationSound: v }).catch(() => {})
            if (v) playCelebrationSound(resolveCelebration(current, isPro))
          }}
          disabled={prefs === undefined}
        />
      </SettingsRow>
    </>
  )
}

function AppearanceSection() {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const setLocaleMutation = useMutation(api.preferences.setLocale).withOptimisticUpdate(
    optimisticSetLocale,
  )
  const [accentUpgradeOpen, setAccentUpgradeOpen] = useState(false)
  const accentPicker = useAccentPicker(() => setAccentUpgradeOpen(true))

  function setLanguage(value: string) {
    localStorage.setItem(LANG_STORAGE_KEY, value)
    i18n.changeLanguage(value)
    void setLocaleMutation({ locale: value })
  }

  const modeOptions = [
    { value: 'light', label: t('settings.themeLight'), icon: <SunIcon className="h-3.5 w-3.5" /> },
    { value: 'dark', label: t('settings.themeDark'), icon: <MoonIcon className="h-3.5 w-3.5" /> },
    {
      value: 'system',
      label: t('settings.themeSystem'),
      icon: <MonitorIcon className="h-3.5 w-3.5" />,
    },
  ] as const

  return (
    <SettingsSection id="appearance" title={t('settings.sec.appearance')}>
      <UpgradeDialog open={accentUpgradeOpen} onOpenChange={setAccentUpgradeOpen} reason="accent" />
      <SettingsGroup>
        <SettingsRow
          id={rowId('settings.theme')}
          icon={<SunIcon className="h-4 w-4" />}
          label={t('settings.theme')}
        >
          <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
            {modeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTheme(opt.value)}
                className={cn(
                  'flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all',
                  theme === opt.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {opt.icon}
                <span className="hidden sm:inline">{opt.label}</span>
              </button>
            ))}
          </div>
        </SettingsRow>

        {/* Label, today's colour, then the swatches on their own full-width row. */}
        <div id={rowId('settings.accentColor')} className="scroll-mt-24">
          <SettingsRow
            icon={<PaletteIcon className="h-4 w-4" />}
            label={t('settings.accentColor')}
            description={accentPicker.line}
          />
          <div className="-mt-1.5 px-4 pb-3.5 sm:pl-16">
            <AccentSwatches {...accentPicker.swatches} />
          </div>
        </div>
        <SettingsRow
          id={rowId('settings.accentRotate')}
          icon={<CalendarIcon className="h-4 w-4" />}
          label={t('settings.accentRotate')}
          description={t('settings.accentRotateDesc')}
        >
          <div className="flex items-center gap-2">
            {accentPicker.rotateSwitch.locked && (
              <LockIcon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2.5} />
            )}
            <Toggle
              checked={accentPicker.rotateSwitch.checked}
              onChange={accentPicker.rotateSwitch.onChange}
              label={t('settings.accentRotate')}
            />
          </div>
        </SettingsRow>
        <CelebrationRows onLocked={() => setAccentUpgradeOpen(true)} />

        <SettingsRow
          id={rowId('settings.language')}
          icon={<GlobeIcon className="h-4 w-4" />}
          label={t('settings.language')}
        >
          <Select value={i18n.language} onValueChange={(v) => v && setLanguage(v)}>
            <SelectTrigger className="cursor-pointer rounded-lg border bg-background text-sm text-foreground">
              <SelectValue>
                {LANGUAGES.find((l) => l.value === i18n.language)?.label ?? i18n.language}
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="end">
              {LANGUAGES.map((l) => (
                <SelectItem key={l.value} value={l.value}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>
      </SettingsGroup>
    </SettingsSection>
  )
}

// ── Today & your day ──────────────────────────────────────────────────────────

function SortableSectionRow({
  id,
  label,
  children,
}: {
  id: TodaySectionKey
  label: string
  children: React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex items-center', isDragging && 'relative z-10 bg-card shadow-md')}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={label}
        className="cursor-grab touch-none py-3.5 pl-3 text-muted-foreground/50 transition-colors hover:text-foreground active:cursor-grabbing"
      >
        <GripVerticalIcon className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

const TODAY_ROWS = {
  focus: {
    field: 'todayShowFocus',
    icon: <TargetIcon className="h-4 w-4" />,
    labelKey: 'settings.showFocus',
    descKey: 'settings.showFocusDesc',
  },
  habits: {
    field: 'todayShowHabits',
    icon: <FlameIcon className="h-4 w-4" />,
    labelKey: 'settings.showHabits',
    descKey: 'settings.showHabitsDesc',
  },
  quick: {
    field: 'todayShowQuick',
    icon: <ZapIcon className="h-4 w-4" />,
    labelKey: 'settings.showQuick',
    descKey: 'settings.showQuickDesc',
  },
} as const

function TodaySection() {
  const { t, i18n: i18nInstance } = useTranslation()
  const prefs = usePrefs()
  const updatePrefs = useUpdatePreferences()
  const setDayStartHour = useViewStore((s) => s.setDayStartHour)
  const loading = prefs === undefined
  const order = resolveTodaySectionOrder(prefs?.todaySectionOrder)
  const dayStart = prefs?.dayStartHour ?? DEFAULT_DAY_START_HOUR
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function save(patch: Parameters<typeof updatePrefs>[0]) {
    updatePrefs(patch).catch(() => toast.error(t('settings.prefError')))
  }

  function setDayStart(h: number) {
    setDayStartHour(h)
    save({ dayStartHour: h })
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    save({
      todaySectionOrder: arrayMove(
        order,
        order.indexOf(active.id as TodaySectionKey),
        order.indexOf(over.id as TodaySectionKey),
      ),
    })
  }

  return (
    <SettingsSection id="today" title={t('settings.sec.today')}>
      <SettingsGroup>
        <SettingsRow
          id={rowId('settings.nightOwl')}
          label={t('settings.nightOwl')}
          description={t('settings.nightOwlDesc')}
        >
          <Toggle
            label={t('settings.nightOwl')}
            checked={dayStart >= NIGHT_OWL_DAY_START_HOUR}
            onChange={(v) => setDayStart(dayStartHourFor(v))}
            disabled={loading}
          />
        </SettingsRow>
        <SettingsRow
          id={rowId('settings.timezone')}
          label={t('settings.timezone')}
          description={`${t('settings.timezoneDetected', { tz: timeZone })} · ${t('settings.timezoneDesc')}`}
        />
        <SettingsRow
          id={rowId('settings.dayStart')}
          label={
            <span className="text-xs font-normal text-muted-foreground">
              {t('settings.dayEndsAt', { time: formatHour(dayStart, i18nInstance.language) })}
            </span>
          }
        >
          <Select
            value={String(dayStart)}
            onValueChange={(v) => v != null && setDayStart(Number(v))}
            disabled={loading}
          >
            <SelectTrigger className="h-7 w-auto cursor-pointer border-0 bg-transparent px-0 text-xs font-semibold text-primary-text shadow-none dark:bg-transparent">
              {t('settings.adjust')}
            </SelectTrigger>
            <SelectContent align="end">
              {range(0, 6).map((h) => (
                <SelectItem key={h} value={String(h)} className="text-xs">
                  {formatHour(h, i18nInstance.language)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>
        <HabitCheckRow />
        <FocusRow />
      </SettingsGroup>

      <SettingsGroup label={t('settings.todaySections')}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((key) => {
              const r = TODAY_ROWS[key]
              return (
                <SortableSectionRow
                  key={key}
                  id={key}
                  label={t('settings.reorderSection', {
                    defaultValue: 'Reorder {{section}}',
                    section: t(r.labelKey),
                  })}
                >
                  <SettingsRow
                    id={rowId(r.labelKey)}
                    icon={r.icon}
                    label={t(r.labelKey)}
                    description={t(r.descKey)}
                  >
                    <Toggle
                      label={t(r.labelKey)}
                      checked={prefs?.[r.field] !== false}
                      onChange={(v) => save({ [r.field]: v })}
                      disabled={loading}
                    />
                  </SettingsRow>
                </SortableSectionRow>
              )
            })}
          </SortableContext>
        </DndContext>
      </SettingsGroup>
    </SettingsSection>
  )
}

function HabitCheckRow() {
  const { t } = useTranslation()
  const style = useHabitCheckStyle()
  const setStyle = useSetHabitCheckStyle()
  return (
    <SettingsRow
      id={rowId('settings.habitCheck')}
      label={t('settings.habitCheck')}
      description={t('settings.habitCheckDesc')}
    >
      <div className="flex shrink-0 items-center gap-1 rounded-lg border bg-muted/50 p-1">
        {(['tap', 'hold'] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={style === v}
            onClick={() => setStyle(v)}
            className={cn(
              'cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-all',
              style === v
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(v === 'tap' ? 'settings.habitCheckTap' : 'settings.habitCheckHold')}
          </button>
        ))}
      </div>
    </SettingsRow>
  )
}

const FOCUS_LABELS = {
  habits: 'settings.focusHabits',
  goals: 'settings.focusGoals',
  both: 'settings.focusBoth',
} as const

function FocusRow() {
  const { t } = useTranslation()
  const prefs = usePrefs()
  const updatePrefs = useUpdatePreferences()
  const focus = prefs?.focus ?? 'both'
  return (
    <SettingsRow
      id={rowId('settings.focus')}
      label={t('settings.focus')}
      description={t('settings.focusDesc')}
    >
      <div className="flex shrink-0 items-center gap-1 rounded-lg border bg-muted/50 p-1">
        {FOCUSES.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={focus === v}
            disabled={prefs === undefined}
            onClick={() =>
              updatePrefs({ focus: v }).catch(() => toast.error(t('settings.prefError')))
            }
            className={cn(
              'cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-all',
              focus === v
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(FOCUS_LABELS[v])}
          </button>
        ))}
      </div>
    </SettingsRow>
  )
}

// ── Take a break ──────────────────────────────────────────────────────────────

const PAUSES = [
  { key: 'pause1d', days: 1 },
  { key: 'pause3d', days: 3 },
  { key: 'pause1w', days: 7 },
] as const

function BreakSection() {
  const { t, i18n: i18nInstance } = useTranslation()
  const prefs = usePrefs()
  const updatePrefs = useUpdatePreferences()
  const { loading, from, until, start, end } = useBreather()
  const isActive = !!from
  const pausedUntil = (prefs?.notifPausedUntil ?? 0) > Date.now() ? prefs?.notifPausedUntil : null

  function pause(until: number | null) {
    updatePrefs({ notifPausedUntil: until }).catch(() => toast.error(t('settings.prefError')))
  }

  async function toggle(on: boolean) {
    try {
      if (on) await start({})
      else await end({})
    } catch {
      toast.error(t('settings.prefError'))
    }
  }

  async function setUntil(value: string) {
    try {
      await start({ from: from ?? undefined, until: value || undefined })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('settings.prefError'))
    }
  }

  const fmt = (key: string) =>
    new Date(`${key}T00:00:00`).toLocaleDateString(i18nInstance.language, {
      month: 'short',
      day: 'numeric',
    })
  const toKey = (d: Date) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
  const [untilOpen, setUntilOpen] = useState(false)
  const statusDesc = isActive
    ? until
      ? t('settings.breatherUntil', { date: fmt(until) })
      : t('settings.breatherIndefinite')
    : t('settings.breatherDesc')

  return (
    <SettingsSection id="break" title={t('settings.sec.break')}>
      <SettingsGroup>
        <SettingsRow
          id={rowId('settings.pauseNotifs')}
          icon={<PauseIcon className="h-4 w-4" />}
          label={t('settings.pauseNotifs')}
          description={
            pausedUntil
              ? t('settings.pausedUntil', {
                  date: new Date(pausedUntil).toLocaleString(i18nInstance.language, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                })
              : t('settings.pauseDesc')
          }
        >
          {pausedUntil ? (
            <button
              type="button"
              onClick={() => pause(null)}
              className="cursor-pointer rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-muted"
            >
              {t('settings.resume')}
            </button>
          ) : (
            <div className="flex flex-wrap justify-end gap-1.5">
              {PAUSES.map(({ key, days }) => (
                <button
                  key={key}
                  type="button"
                  disabled={!prefs}
                  onClick={() => pause(Date.now() + days * 86_400_000)}
                  className="cursor-pointer rounded-full border px-2.5 py-1 text-xs transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:opacity-50"
                >
                  {t(`settings.${key}`)}
                </button>
              ))}
            </div>
          )}
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup>
        <SettingsRow
          id={rowId('settings.breatherLabel')}
          icon={<SproutIcon className="h-4 w-4" />}
          label={t('settings.breather')}
          description={statusDesc}
        >
          <Toggle
            label={t('settings.breatherLabel')}
            checked={isActive}
            onChange={toggle}
            disabled={loading}
          />
        </SettingsRow>
        {isActive && (
          <SettingsRow
            icon={<CalendarIcon className="h-4 w-4" />}
            label={t('settings.breatherEndDate')}
            description={t('settings.breatherEndDateDesc')}
          >
            <Popover open={untilOpen} onOpenChange={setUntilOpen}>
              <PopoverTrigger className="flex h-8 w-auto cursor-pointer items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-sm text-foreground transition-colors dark:bg-input/30 dark:hover:bg-input/50">
                {until ? fmt(until) : t('settings.breatherNoEnd')}
                <ChevronDownIcon className="h-4 w-4 opacity-50" />
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  disabled={from ? { before: new Date(`${from}T00:00:00`) } : undefined}
                  selected={until ? new Date(`${until}T00:00:00`) : undefined}
                  defaultMonth={until ? new Date(`${until}T00:00:00`) : new Date()}
                  onSelect={(d) => {
                    if (d) void setUntil(toKey(d))
                    setUntilOpen(false)
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    void setUntil('')
                    setUntilOpen(false)
                  }}
                  className="w-full cursor-pointer border-t border-border px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted"
                >
                  {t('settings.breatherNoEnd')}
                </button>
              </PopoverContent>
            </Popover>
          </SettingsRow>
        )}
      </SettingsGroup>
    </SettingsSection>
  )
}

// ── Connected apps ────────────────────────────────────────────────────────────

function AppsSection() {
  const { t } = useTranslation()
  const wearables = useQuery(api.fitness.available)
  const appsAvailable = useQuery(api.apps.available)
  const location = useQuery(api.userLocation.get)
  const storeLocation = useMutation(api.userLocation.store).withOptimisticUpdate(
    (store, { city, country }) =>
      store.setQuery(api.userLocation.get, {}, { city, country, capturedAt: Date.now() }),
  )
  const [devOpen, setDevOpen] = useState(false)
  return (
    <SettingsSection id="apps" title={t('settings.sec.apps')}>
      <div id={rowId('settings.calendar')} className="scroll-mt-24">
        <SettingsGroup label={t('settings.calendar')}>
          <CalendarSettings />
        </SettingsGroup>
      </div>
      {(appsAvailable?.instagram || appsAvailable?.gmail) && (
        <div id={rowId('settings.apps')} className="scroll-mt-24">
          <SettingsGroup label={t('settings.apps.title')}>
            <AppConnectionsSettings />
          </SettingsGroup>
        </div>
      )}
      <div id={rowId('inboundEmail.title')} className="scroll-mt-24">
        <SettingsGroup label={t('inboundEmail.title')}>
          <EmailToGoaltreeSettings />
        </SettingsGroup>
      </div>
      {/* Parked until a free web route covers Android; shows once a provider is configured. */}
      {(wearables?.fitbit || wearables?.strava) && (
        <div id={rowId('fitness.title')} className="scroll-mt-24">
          <SettingsGroup label={t('fitness.title')}>
            <WearablesSettings />
          </SettingsGroup>
        </div>
      )}
      <div id={rowId('settings.location')} className="scroll-mt-24">
        <SettingsGroup label={t('settings.location')}>
          <div className="px-4 py-3">
            <LocationSettings
              location={location ?? null}
              onSave={(city, country, lat, lon) =>
                void storeLocation({ city, country, latitude: lat, longitude: lon })
              }
            />
          </div>
        </SettingsGroup>
      </div>
      <SettingsGroup>
        <SettingsRow
          id={rowId('settings.forDevelopers')}
          icon={<CodeIcon className="h-4 w-4" />}
          label={t('settings.forDevelopers')}
          description={t('settings.forDevelopersDesc')}
          onClick={() => setDevOpen((o) => !o)}
        >
          <ChevronRightIcon
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              devOpen && 'rotate-90',
            )}
          />
        </SettingsRow>
        {devOpen && <McpSection />}
      </SettingsGroup>
    </SettingsSection>
  )
}

// ── Account ───────────────────────────────────────────────────────────────────

function AccountSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { unsubscribe } = useWebPush()
  const exportMyData = useAction(api.dataExport.exportMyData)
  const [exporting, setExporting] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteText, setDeleteText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const deleteAccount = useMutation(api.nodes.deleteAccount)

  async function handleExport() {
    setExporting(true)
    try {
      const { url, filename } = await exportMyData({})
      // Fetch first: the download attribute is ignored on cross-origin links.
      const blob = await (await fetch(url)).blob()
      const href = URL.createObjectURL(blob)
      const a = Object.assign(document.createElement('a'), { href, download: filename })
      a.click()
      URL.revokeObjectURL(href)
    } catch {
      toast.error(t('settings.exportError'))
    } finally {
      setExporting(false)
    }
  }

  async function handleSignOut() {
    setSigningOut(true)
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          clearSessionIndicator()
          clearOfflineData()
          navigate({ to: '/auth/login' })
        },
      },
    })
    setSigningOut(false)
  }

  async function handleDeleteAccount() {
    setDeleting(true)
    // Deleting wipes goals/prefs while still authenticated, which would otherwise
    // trip the onboarding redirect in the authed layout. Flag it to suppress that
    // during the SPA phase before we hard-redirect away.
    sessionStorage.setItem('accountDeleting', '1')
    try {
      // Unsubscribe this device's web-push subscription so notifications actually
      // stop — the server record is purged below, but the browser subscription
      // must be torn down too or it lingers. Best-effort; never block deletion.
      await unsubscribe().catch(() => {})
      await deleteAccount({})
      await authClient.deleteUser({})
      // Explicitly clear the session so the login route doesn't see a stale
      // isAuthenticated:true and bounce back into the (now-empty) app.
      await authClient.signOut().catch(() => {})
      clearSessionIndicator()
      clearOfflineData()
      // Hard redirect: full reload drops all reactive state, sidestepping the
      // delete-race entirely.
      window.location.href = '/auth/login'
    } catch {
      sessionStorage.removeItem('accountDeleting')
      toast.error('Failed to delete account — please try again.')
      setDeleting(false)
    }
  }

  const spin = (
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
  )

  return (
    <SettingsSection id="account" title={t('settings.sec.account')}>
      <SettingsGroup>
        <SettingsRow
          id={rowId('appReview.settingsRow')}
          icon={<StarIcon className="h-4 w-4" />}
          label={t('appReview.settingsRow')}
          description={t('appReview.settingsRowDesc')}
          onClick={() => setReviewOpen(true)}
        />
        <SettingsRow
          id={rowId('settings.exportData')}
          icon={exporting ? spin : <DownloadIcon className="h-4 w-4" />}
          label={t('settings.exportData')}
          description={t('settings.exportDataDesc')}
          onClick={exporting ? undefined : handleExport}
        >
          <span className="text-xs font-semibold text-primary-text">{t('settings.export')}</span>
        </SettingsRow>
        <SettingsRow
          id={rowId('settings.signOut')}
          icon={signingOut ? spin : <LogOutIcon className="h-4 w-4" />}
          label={t('settings.signOut')}
          onClick={signingOut ? undefined : handleSignOut}
        />
      </SettingsGroup>

      <SettingsGroup label={t('settings.dangerZone')} danger>
        <SettingsRow
          id={rowId('settings.deleteAccount')}
          icon={deleting ? spin : <TrashIcon className="h-4 w-4" />}
          label={t('settings.deleteAccount')}
          description={t('settings.deleteTypeHint')}
          onClick={
            deleting
              ? undefined
              : () => {
                  setDeleteText('')
                  setConfirmDelete(true)
                }
          }
          destructive
        />
      </SettingsGroup>

      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.deleteAccountTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('settings.deleteAccountDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <label className="space-y-1.5 text-xs text-muted-foreground">
            <span>{t('settings.deleteTypeHint')}</span>
            <input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="DELETE"
              autoCapitalize="characters"
              autoComplete="off"
              className="w-full rounded-lg border bg-background px-3 py-2 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-destructive/40"
            />
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteText !== 'DELETE'}
              onClick={() => {
                setConfirmDelete(false)
                void handleDeleteAccount()
              }}
            >
              {t('settings.deleteAccount')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  )
}

// ── For developers (MCP) ──────────────────────────────────────────────────────

const MCP_CLIENT_ID = 'claude-ai-goaltree'
const CLAUDE_CONNECTORS_URL = 'https://claude.ai/settings/connectors'

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-muted px-2 py-1.5 text-xs font-mono">
          {value}
        </code>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(value)
            toast.success(t('settings.mcpCopied'))
          }}
          className="shrink-0 cursor-pointer rounded-md border p-1.5 hover:bg-muted transition-colors"
        >
          <CopyIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// OAuth grants from Claude, ChatGPT and other MCP clients; disconnecting
// deletes their access and refresh tokens.
function ConnectedApps() {
  const { t } = useTranslation()
  const apps = useQuery(api.mcpTokens.listOAuthConnections) ?? []
  const revoke = useMutation(api.mcpTokens.revokeOAuthConnection).withOptimisticUpdate(
    (store, { clientId }) => {
      const cur = store.getQuery(api.mcpTokens.listOAuthConnections, {})
      if (cur)
        store.setQuery(
          api.mcpTokens.listOAuthConnections,
          {},
          cur.filter((a) => a.clientId !== clientId),
        )
    },
  )
  if (apps.length === 0) return null
  return (
    <div className="space-y-3 border-t px-4 py-3.5">
      <div>
        <p className="text-sm font-medium">{t('settings.mcpConnectedTitle')}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.mcpConnectedDesc')}</p>
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-lg border">
        {apps.map((app) => (
          <div key={app.clientId} className="flex items-center gap-3 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{app.name}</p>
              <p className="text-xs text-muted-foreground">
                {t('settings.mcpConnectedSince', {
                  date: new Date(app.connectedAt).toLocaleDateString(),
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                revoke({ clientId: app.clientId })
                  .then(() => toast.success(t('settings.mcpDisconnected', { name: app.name })))
                  .catch(() => toast.error(t('settings.mcpRevokeError')))
              }
              className="shrink-0 cursor-pointer rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              {t('settings.mcpDisconnect')}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function McpSection() {
  const { t } = useTranslation()
  const createToken = useMutation(api.mcpTokens.create)
  const revokeToken = useMutation(api.mcpTokens.revoke).withOptimisticUpdate(
    (store, { tokenId }) => {
      const cur = store.getQuery(api.mcpTokens.list, {})
      if (cur)
        store.setQuery(
          api.mcpTokens.list,
          {},
          cur.filter((tok) => tok._id !== tokenId),
        )
    },
  )
  const tokens = useQuery(api.mcpTokens.list) ?? []
  const [newTokenName, setNewTokenName] = useState('')
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const mcpUrl = import.meta.env.VITE_MCP_URL ?? `${import.meta.env.VITE_CONVEX_SITE_URL}/mcp`

  async function handleCreate() {
    if (!newTokenName.trim()) return
    setCreating(true)
    try {
      const raw = 'gtmcp_' + crypto.randomUUID().replace(/-/g, '')
      await createToken({ token: raw, name: newTokenName.trim() })
      setGeneratedToken(raw)
      setNewTokenName('')
      setShowForm(false)
    } catch {
      toast.error(t('settings.mcpCreateError'))
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(tokenId: string) {
    try {
      await revokeToken({ tokenId: tokenId as Parameters<typeof revokeToken>[0]['tokenId'] })
      setGeneratedToken(null)
    } catch {
      toast.error(t('settings.mcpRevokeError'))
    }
  }

  return (
    <>
      {/* Claude.ai OAuth connector */}
      <div className="space-y-3 px-4 py-3.5">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium">{t('settings.mcpClaudeTitle')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.mcpClaudeDesc')}</p>
          </div>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#d4956a]/10 text-[#d4956a]">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.24 3H14.4L9 21h2.83l1.17-3.67h5l1.17 3.67H22L17.24 3zm-3.55 12L15.5 8.3l1.81 6.7h-3.62zM3 8h4v2H3v7H1V5h8v2H3v1z" />
            </svg>
          </div>
        </div>

        <CopyField label={t('settings.mcpServerUrl')} value={mcpUrl} />
        <CopyField label={t('settings.mcpClientId')} value={MCP_CLIENT_ID} />

        <p className="text-xs text-muted-foreground/60 italic">
          {t('settings.mcpClientSecretNote')}
        </p>

        <a
          href={CLAUDE_CONNECTORS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-text hover:underline"
        >
          <ExternalLinkIcon className="h-3 w-3" />
          {t('settings.mcpOpenConnectors')}
        </a>
      </div>

      <ConnectedApps />

      {/* API tokens divider */}
      <div className="border-t px-4 py-3.5 space-y-4">
        <div>
          <p className="text-sm font-medium">{t('settings.mcpApiTokensTitle')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('settings.mcpApiTokensDesc')}</p>
        </div>

        {generatedToken && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
            <p className="text-xs font-semibold text-primary-text">
              {t('settings.mcpTokenCopyHint')}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs font-mono">
                {generatedToken}
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(generatedToken)
                  toast.success(t('settings.mcpCopied'))
                }}
                className="shrink-0 cursor-pointer rounded-md border p-1.5 hover:bg-muted transition-colors"
              >
                <CopyIcon className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('settings.mcpEndpoint')}:{' '}
              <code className="rounded bg-muted px-1 text-xs">{mcpUrl}</code>
            </p>
          </div>
        )}

        {tokens.length > 0 && (
          <div className="divide-y divide-border rounded-lg border overflow-hidden">
            {tokens.map((tok) => (
              <div key={tok._id} className="flex items-center gap-3 px-3 py-2.5">
                <KeyRoundIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{tok.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {tok.preview} ·{' '}
                    {tok.lastUsedAt
                      ? t('settings.mcpLastUsed', {
                          date: new Date(tok.lastUsedAt).toLocaleDateString(),
                        })
                      : t('settings.mcpCreated', {
                          date: new Date(tok.createdAt).toLocaleDateString(),
                        })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRevoke(tok._id)}
                  className="shrink-0 cursor-pointer rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                  aria-label={t('settings.mcpRevoke')}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {showForm ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              placeholder={t('settings.mcpTokenNamePlaceholder')}
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
                if (e.key === 'Escape') setShowForm(false)
              }}
              className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !newTokenName.trim()}
              className="cursor-pointer rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white dark:text-[#0a0a0a] disabled:opacity-50 transition-opacity"
            >
              {creating ? '…' : t('settings.mcpCreate')}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="cursor-pointer rounded-lg border px-3 py-2 text-xs transition-colors hover:bg-muted"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setGeneratedToken(null)
              setShowForm(true)
            }}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
          >
            <KeyRoundIcon className="h-3.5 w-3.5" />
            {t('settings.mcpNewToken')}
          </button>
        )}
      </div>
    </>
  )
}

// ── Navigation & search ───────────────────────────────────────────────────────

// Searchable rows as [label, description?, extra match?] i18n keys; ids come from rowId(label).
const SEARCH_INDEX: Record<SettingsSectionId, [string, string?, string?][]> = {
  profile: [
    ['settings.changePhoto'],
    ['settings.publicProfile'],
    ['settings.live.friends', 'settings.live.friendsDesc', 'settings.live.title'],
    ['settings.live.global', 'settings.live.globalDesc', 'settings.live.title'],
    ['settings.leaderboard.show', 'settings.leaderboard.showDesc', 'nav.leaderboard'],
    ['progression.section', 'progression.badges'],
    ['stories.blocks.title', 'stories.blocks.desc'],
  ],
  appearance: [
    ['settings.theme'],
    ['settings.accentColor', 'settings.accentRotate', 'settings.accentRotateDesc'],
    ['settings.celebration', 'settings.celebrationDesc'],
    ['settings.celebrationSound', 'settings.celebrationSoundDesc'],
    ['settings.language'],
  ],
  coach: [
    ['coach.voice', 'settings.chooseVoice'],
    ['coach.nameField.label', 'coach.nameField.hint', 'coach.nameField.rename'],
    ['coach.tone', 'coach.toneHint.adaptive'],
    ['coach.readAloud'],
    ['coachCall.setting', 'coachCall.settingDesc'],
    ['coachSee.label', 'coachSee.desc'],
    ['settings.clonedVoice', 'settings.clonedVoiceDesc'],
  ],
  mycoach: [
    ['coaching.settings.inviteTitle', 'coaching.settings.inviteDesc'],
    ['coaching.settings.habits', 'coaching.settings.habitsDesc'],
    ['coaching.settings.checkins', 'coaching.settings.checkinsDesc'],
    ['coaching.settings.end'],
  ],
  notifications: [
    ...NOTIF_GROUPS.flatMap((g) =>
      g.rows.map((r): [string, string, string] => [
        `settings.label.${r.key}`,
        `settings.ex.${r.key}`,
        `settings.group.${g.group}`,
      ]),
    ),
    ['settings.quietLabel', 'settings.quietDesc', 'settings.group.quiet'],
  ],
  today: [
    ['settings.nightOwl', 'settings.nightOwlDesc'],
    ['settings.timezone'],
    ['settings.dayStart', 'settings.dayStartDesc'],
    ['settings.habitCheck', 'settings.habitCheckDesc'],
    ['settings.focus', 'settings.focusDesc'],
    ['settings.showFocus', 'settings.showFocusDesc'],
    ['settings.showHabits', 'settings.showHabitsDesc'],
    ['settings.showQuick', 'settings.showQuickDesc'],
  ],
  break: [
    ['settings.pauseNotifs', 'settings.pauseDesc'],
    ['settings.breatherLabel', 'settings.breatherDesc'],
  ],
  plan: [['settings.plan', 'settings.upgrade'], ['settings.usage']],
  apps: [
    ['settings.calendar', 'settings.calendar.description'],
    ['inboundEmail.title', 'inboundEmail.sendersHint'],
    ['settings.location', 'settings.location.description'],
    ['settings.forDevelopers', 'settings.forDevelopersDesc'],
  ],
  account: [
    ['appReview.settingsRow', 'appReview.settingsRowDesc'],
    ['settings.exportData', 'settings.exportDataDesc'],
    ['settings.signOut'],
    ['settings.deleteAccount'],
  ],
}

const scrollToTop = () => document.querySelector('[data-scroll-reset]')?.scrollTo({ top: 0 })

// Sections (and their indexed rows) matching the settings search, or null when empty.
function useSearch(query: string) {
  const { t } = useTranslation()
  const q = query.trim().toLocaleLowerCase()
  if (!q) return null
  const hit = (key?: string) => !!key && t(key).toLocaleLowerCase().includes(q)
  const sections = new Set<SettingsSectionId>()
  const rows: string[] = []
  for (const section of SETTINGS_SECTIONS) {
    const titleHit = t(`settings.sec.${section}`).toLocaleLowerCase().includes(q)
    for (const [label, desc, extra] of SEARCH_INDEX[section]) {
      if (hit(label) || hit(desc) || hit(extra)) {
        sections.add(section)
        rows.push(rowId(label))
      }
    }
    if (titleHit) sections.add(section)
  }
  return { sections, rows }
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation()
  return (
    <label className="flex h-11 items-center gap-2.5 rounded-xl border bg-card px-3.5 text-sm focus-within:ring-2 focus-within:ring-primary/30">
      <SearchIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onChange('')}
        placeholder={t('settings.search')}
        aria-label={t('settings.search')}
        className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('common.close')}
          className="cursor-pointer text-muted-foreground hover:text-foreground"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </label>
  )
}

const SECTION_COMPONENTS: Record<SettingsSectionId, () => React.ReactNode> = {
  profile: ProfileSection,
  appearance: AppearanceSection,
  coach: CoachSection,
  mycoach: MyCoachSection,
  notifications: NotificationsSection,
  today: TodaySection,
  break: BreakSection,
  plan: PlanSection,
  apps: AppsSection,
  account: AccountSection,
}

function RouteComponent() {
  const { t } = useTranslation()
  const { onScroll } = useScrollTitle(t('settings.title'))
  const [query, setQuery] = useState('')
  const matches = useSearch(query)
  const coaching = useIsCoaching()

  useEffect(() => {
    const id = window.location.hash.slice(1)
    if (!id) return
    // After the route's scroll reset, so the deep-linked section stays in view.
    const raf = requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView({ block: 'start' }),
    )
    return () => cancelAnimationFrame(raf)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const calendarParam = params.get('calendar')
    if (!calendarParam) return
    if (calendarParam === 'connected') {
      toast.success(t('settings.calendar.connectedToast'))
    } else if (calendarParam === 'error') {
      toast.error(t('settings.calendar.connectError'))
    }
    params.delete('calendar')
    const newSearch = params.toString()
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash,
    )
  }, [t])

  // Typing jumps back to the top so the filtered sections are in view.
  useEffect(() => {
    if (query) scrollToTop()
  }, [query])

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('settings.title')} showInbox={false} showCoach />
        <SiteHeader className="hidden lg:flex" />
        <div
          className="overflow-y-auto px-4 pb-16 lg:pb-0"
          onScroll={onScroll}
          data-scroll-reset
          data-scroll-restoration-id="tab-scroll"
        >
          <div className="mx-auto max-w-(--page-col) pb-6 lg:pb-10">
            {/* Top gap lives inside the sticky bar, so it sits still instead of
                sliding up before it sticks. */}
            <div className="sticky top-(--app-hdr-h) z-10 -mx-1 bg-background/95 px-1 pt-6 pb-2 backdrop-blur lg:pt-10">
              <SearchBox value={query} onChange={setQuery} />
            </div>
            {/* Highlight the matching rows inside the sections that stay visible. */}
            {matches && matches.rows.length > 0 && (
              <style>{`${matches.rows.map((r) => `#${r}`).join(',')}{background:color-mix(in srgb,var(--primary) 9%,transparent)}`}</style>
            )}
            <div className="mt-4 space-y-12">
              {!matches && <CoachingModeSettings />}
              {SETTINGS_SECTIONS.map((id) => {
                const Section = SECTION_COMPONENTS[id]
                return (
                  <div
                    key={id}
                    hidden={
                      (!!matches && !matches.sections.has(id)) || (coaching && id === 'coach')
                    }
                  >
                    <Section />
                  </div>
                )
              })}
              {matches && matches.sections.size === 0 && (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {t('settings.searchEmpty', { query: query.trim() })}
                </p>
              )}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
